/**
 * FilterBar Component
 *
 * Professional property filtering system inspired by Arrived.com
 * Features: Property type chips, location search, price range filters
 */

import React from 'react';
import { Search } from 'lucide-react';
import { CategoryFilter } from './CategoryFilter';

export interface FilterState {
  propertyType: string;
  searchQuery: string;
  minPrice: number;
  maxPrice: number;
  location: string;
}

export interface CategoryOption {
  value: string;
  label: string;
}

interface FilterBarProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  propertyCount?: number;
  categories?: CategoryOption[];
  /** When false, the category capsule row is not rendered here (e.g. it's rendered
   *  separately as a sticky bar). Default true. */
  showCategories?: boolean;
}

export const DEFAULT_CATEGORIES: CategoryOption[] = [
  { value: 'all', label: 'All' },
  { value: 'single-family', label: 'Single Family' },
  { value: 'vacation-rental', label: 'Vacation Rental' },
  { value: 'multi-family', label: 'Multi-Family' },
  { value: 'fund', label: 'Funds' },
];

export function resolveCategories(categories?: CategoryOption[]): CategoryOption[] {
  return categories && categories.length > 0 ? categories : DEFAULT_CATEGORIES;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFilterChange,
  propertyCount = 0,
  categories,
  showCategories = true,
}) => {
  const displayCategories = resolveCategories(categories);

  const handleSearchChange = (query: string) => {
    onFilterChange({ ...filters, searchQuery: query });
  };

  return (
    <div className="bg-white">
      <div className="px-0 py-4">
        {/* Property Type Chips - Arrived.com style */}
        {showCategories && (
          <CategoryFilter
            value={filters.propertyType}
            onChange={(type) => onFilterChange({ ...filters, propertyType: type })}
            categories={displayCategories}
            className="mb-4"
          />
        )}

        {/* Search and Advanced Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Bar */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by city, state, or property..."
              value={filters.searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-border rounded-md bg-white text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
          </div>

          {/* Advanced Filters Button - Arrived.com style */
          /* TODO: Renable post mvp */
          }
          {/* <button className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-all whitespace-nowrap">
            <SlidersHorizontal className="w-4 h-4" />
            <span className="text-sm font-medium">Filters</span>
          </button> */}
        </div>

        {/* Results Count */}
        {propertyCount > 0 && (
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
          </div>
        )}
      </div>
    </div>
  );
};
